import { useCallback, useMemo, useState } from 'react'

import { Alert, Button, Group, List, Modal, Stack, Text, TextInput, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react'
import { useTranslate } from 'react-admin'

import { MonetizationPatternAttributesApiDTO } from '../../codegen/generated/Api.schemas'
import {
  useDeleteApiMonetizationPatternsId,
  useGetApiMonetizationPatterns,
} from '../../codegen/generated/monetization-patterns'
import { LXTable, LXTableColumn } from '../../components/LXTable/LXTable'
import { MonetizationPatternsHelper } from '../../helpers/MonetizationPatterns.helper'
import { MonetizationPatternForm } from './MonetizationPatternForm'

type LXMonetizationPatternsItemTableType = Pick<
  MonetizationPatternAttributesApiDTO,
  'id' | 'name' | 'hasPreroll' | 'hasPostroll'
> & {
  fixedMidrolls: JSX.Element
  recurringMidroll: JSX.Element
}

export const MonetizationPatterns = (): JSX.Element => {
  const t = useTranslate()

  const [search, setSearch] = useState('')
  const [isFormModalOpened, { open: openFormModal, close: closeFormModal }] = useDisclosure(false)
  const [editedPattern, setEditedPattern] = useState<MonetizationPatternAttributesApiDTO>()
  const [deletedPattern, setDeletedPattern] = useState<MonetizationPatternAttributesApiDTO>()

  const patternsQuery = useGetApiMonetizationPatterns()
  const deleteMutation = useDeleteApiMonetizationPatternsId()

  const patterns = useMemo(() => patternsQuery.data?.body ?? [], [patternsQuery.data?.body])

  const onCreate = useCallback(() => {
    setEditedPattern(undefined)
    openFormModal()
  }, [openFormModal])

  const onEdit = useCallback(
    (id: MonetizationPatternAttributesApiDTO['id']) => {
      setEditedPattern(patterns.find(p => p.id === id))
      openFormModal()
    },
    [openFormModal, patterns],
  )

  const onFormSuccess = useCallback(() => {
    closeFormModal()
    patternsQuery.refetch()
  }, [closeFormModal, patternsQuery])

  const onDelete = useCallback(async () => {
    if (!deletedPattern) return

    try {
      await deleteMutation.mutateAsync({ id: deletedPattern.id })

      notifications.show({
        color: 'green',
        title: t('pages.monetizationPatterns.delete.title'),
        message: t('pages.monetizationPatterns.delete.message', { name: deletedPattern.name }),
      })
      setDeletedPattern(undefined)
      patternsQuery.refetch()
    } catch (error: any) {
      console.error(error)
      notifications.show({
        color: 'red',
        title: `${t('_generics.error')} - ${error.response?.status ?? ''}`,
        message:
          error.response?.data?.content?.message ??
          error.response?.data?.message ??
          t('_generics.unknownError'),
      })
    }
  }, [deleteMutation, deletedPattern, patternsQuery, t])

  const columns: LXTableColumn<LXMonetizationPatternsItemTableType>[] = useMemo(
    () => [
      { key: 'name', value: t('_generics.name') },
      { key: 'hasPreroll', value: t('pages.monetizationPatterns.hasPreroll'), type: 'boolean' },
      { key: 'hasPostroll', value: t('pages.monetizationPatterns.hasPostroll'), type: 'boolean' },
      {
        key: 'fixedMidrolls',
        value: t('pages.monetizationPatterns.fixedMidrolls'),
        type: 'component',
      },
      {
        key: 'recurringMidroll',
        value: t('pages.monetizationPatterns.recurringMidroll'),
        type: 'component',
      },
    ],
    [t],
  )

  const items = useMemo(
    () =>
      patterns
        .filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase()))
        .map<LXMonetizationPatternsItemTableType>(p => ({
          id: p.id,
          name: p.name,
          hasPreroll: p.hasPreroll,
          hasPostroll: p.hasPostroll,
          fixedMidrolls: (
            <Text size="sm">
              {p.fixedMidrollSeconds.length > 0
                ? MonetizationPatternsHelper.formatFixedMidrolls(p.fixedMidrollSeconds)
                : '—'}
            </Text>
          ),
          recurringMidroll: (
            <Text size="sm">
              {typeof p.recurringMidrollStartSeconds === 'number' &&
              typeof p.recurringMidrollIntervalSeconds === 'number'
                ? t('pages.monetizationPatterns.recurringSummary', {
                    start: MonetizationPatternsHelper.formatTimecode(
                      p.recurringMidrollStartSeconds,
                    ),
                    interval: MonetizationPatternsHelper.formatTimecode(
                      p.recurringMidrollIntervalSeconds,
                    ),
                  })
                : '—'}
            </Text>
          ),
        })),
    [patterns, search, t],
  )

  const setActions = useCallback(
    (item: LXMonetizationPatternsItemTableType) => [
      <Button
        key="edit"
        variant="subtle"
        size="xs"
        leftSection={<IconPencil size={12} />}
        onClick={event => {
          event.stopPropagation()
          onEdit(item.id)
        }}
      >
        <Text size="xs" tt="uppercase">
          {t('_generics.edit')}
        </Text>
      </Button>,
      <Button
        key="delete"
        variant="subtle"
        size="xs"
        color="red"
        leftSection={<IconTrash size={12} />}
        onClick={event => {
          event.stopPropagation()
          setDeletedPattern(patterns.find(p => p.id === item.id))
        }}
      >
        <Text size="xs" tt="uppercase">
          {t('_generics.delete')}
        </Text>
      </Button>,
    ],
    [onEdit, patterns, t],
  )

  return (
    <Stack gap="sm" mt="md">
      <Alert
        icon={<IconInfoCircle size={20} />}
        title={t('pages.monetizationPatterns.about.title')}
        color="blue"
        styles={{
          body: { lineHeight: 1 },
        }}
        mb="sm"
      >
        {t('pages.monetizationPatterns.about.description')}
      </Alert>
      <Group justify="space-between">
        <TextInput
          size="xs"
          w={300}
          value={search}
          placeholder={t('pages.monetizationPatterns.search')}
          leftSection={<IconSearch size={14} />}
          onChange={event => setSearch(event.currentTarget.value)}
        />
        <Group gap="sm">
          <Text size="xs" c="dimmed">
            {t('pages.monetizationPatterns.patternsCount', { smart_count: items.length })}
          </Text>
          <Button
            type="button"
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={onCreate}
          >
            <Text size="xs" tt="uppercase">
              {t('pages.monetizationPatterns.create.button')}
            </Text>
          </Button>
        </Group>
      </Group>
      <LXTable<LXMonetizationPatternsItemTableType>
        items={items}
        columns={columns}
        setActions={setActions}
        loading={patternsQuery.isLoading}
        onRowClick={item => onEdit(item.id)}
      />

      <Modal
        title={
          <Title size="h3">
            {editedPattern
              ? t('pages.monetizationPatterns.editModal.title')
              : t('pages.monetizationPatterns.createModal.title')}
          </Title>
        }
        opened={isFormModalOpened}
        onClose={closeFormModal}
        size="lg"
      >
        {isFormModalOpened && (
          <MonetizationPatternForm
            key={editedPattern?.id ?? 'new'}
            pattern={editedPattern}
            onSuccess={onFormSuccess}
            onClose={closeFormModal}
          />
        )}
      </Modal>

      <Modal
        title={
          <Title order={3}>
            {t('pages.monetizationPatterns.deleteModal.title', { name: deletedPattern?.name })}
          </Title>
        }
        opened={!!deletedPattern}
        onClose={() => setDeletedPattern(undefined)}
        size={720}
        padding="lg"
        centered
      >
        <Stack gap="xl">
          <Alert
            icon={<IconAlertTriangle size={24} />}
            color="red"
            variant="light"
            title={
              <Text size="md" fw={700}>
                {t('pages.monetizationPatterns.deleteModal.consequencesTitle')}
              </Text>
            }
          >
            <List spacing="sm">
              <List.Item>
                {t('pages.monetizationPatterns.deleteModal.consequencePatternRemoved')}
              </List.Item>
              <List.Item>
                {t('pages.monetizationPatterns.deleteModal.consequenceAutoMonetizationDisabled')}
              </List.Item>
              <List.Item>
                {t('pages.monetizationPatterns.deleteModal.consequenceReconfigure')}
              </List.Item>
            </List>
          </Alert>

          <Group justify="flex-end" gap="md">
            <Button variant="subtle" onClick={() => setDeletedPattern(undefined)}>
              {t('_generics.cancel')}
            </Button>
            <Button color="red" loading={deleteMutation.isLoading} onClick={onDelete}>
              {t('pages.monetizationPatterns.deleteModal.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
